import { useCallback, useState } from 'react';
import type { ChangeEvent, Dispatch, SetStateAction } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import type { WorkspaceMembersResult } from '@cbhq/identity-api-hooks/account-management/fetch';

import type { InviteEmailErrorKind } from '../../utils/validateInviteEmail';
import { validateInviteEmail } from '../../utils/validateInviteEmail';

const messages = defineMessages<NonNullable<InviteEmailErrorKind>>({
  required: {
    id: 'Team.inviteTray.emailRequiredError',
    defaultMessage: 'Email is required',
    description: 'Inline error shown when the invite email is empty on submit',
  },
  invalid: {
    id: 'Team.inviteTray.emailInvalidError',
    defaultMessage: 'Invalid email address',
    description: 'Inline error shown when the invite email is malformed on submit',
  },
  selfInvite: {
    id: 'Team.inviteTray.emailSelfInviteError',
    defaultMessage: 'You can’t invite yourself',
    description: 'Inline error shown when the invite email matches the logged-in user',
  },
  alreadyMember: {
    id: 'Team.inviteTray.emailAlreadyMemberError',
    defaultMessage: 'This person is already a member of your team',
    description: 'Inline error shown when the invite email belongs to an existing team member',
  },
  alreadyInvited: {
    id: 'Team.inviteTray.emailAlreadyInvitedError',
    defaultMessage: 'This person has already been invited',
    description: 'Inline error shown when the invite email already has a pending invite',
  },
});

type UseInviteEmailFieldParams = {
  currentUserEmail?: string;
  members?: WorkspaceMembersResult['members'];
  pendingInvites?: WorkspaceMembersResult['pendingInvites'];
};

export type UseInviteEmailFieldResult = {
  email: string;
  emailError: string | null;
  setEmailError: Dispatch<SetStateAction<string | null>>;
  handleEmailChange: (event: ChangeEvent<HTMLInputElement>) => void;
  /** Pure: derives the error message (or null) from the current inputs. Does not mutate state. */
  validate: () => string | null;
};

export function useInviteEmailField({
  currentUserEmail,
  members,
  pendingInvites,
}: UseInviteEmailFieldParams): UseInviteEmailFieldResult {
  const { formatMessage } = useIntl();
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);

  const handleEmailChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.value);
    setEmailError(null);
  }, []);

  const validate = useCallback((): string | null => {
    const kind = validateInviteEmail({ email, currentUserEmail, members, pendingInvites });
    return kind ? formatMessage(messages[kind]) : null;
  }, [currentUserEmail, email, formatMessage, members, pendingInvites]);

  return { email, emailError, setEmailError, handleEmailChange, validate };
}
